@file:Suppress("UnsafeCastFromDynamic")

package com.zi.support.web

import com.zi.support.shared.ReleaseInfo
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent

private data class Page(val id: String, val title: String, val icon: String, val subtitle: String)

internal object WebApp {
    // Email delivery is not configured yet, so keep the deployed portal on the
    // existing username/password flow. The OTP implementation remains ready.
    private const val WEB_OTP_ENABLED = false
    private val pages = listOf(
        Page("dashboard", "Dashboard", "space_dashboard", "Your support workload and recent activity"),
        Page("issues", "Issues", "error", "Technical incidents reported by connected applications"),
        Page("tickets", "Tickets", "confirmation_number", "Assign, collaborate on and resolve support work"),
        Page("kanban", "Kanban", "view_kanban", "Projects and tasks on the team board"),
        Page("meetings", "Meetings", "calendar_month", "Schedule meetings with internal and external attendees"),
        Page("visits", "Site visits", "location_on", "Customer visit records linked to tickets"),
        Page("checklists", "Checklists", "fact_check", "Signed application training records"),
        Page("chat", "Team chat", "chat_bubble", "Private messages and groups"),
        Page("notifications", "Notifications", "notifications", "Assignments and updates you may have missed"),
        Page("portals", "Portals", "language", "Customer portal links, grouped by company"),
        Page("more", "More", "more_horiz", "Tools and connectivity"),
        // Opened from the top-bar buttons, not the sidebar.
        Page("calls", "Calls", "call", "Every call captured by the support phones"),
        Page("settings", "Settings", "settings", "Your profile, appearance and the team directory")
    )
    private val sidebarPages get() = pages.filter { it.id != "calls" && it.id != "settings" }
    /** Sidebar sections; labels show when the sidebar is expanded, spacing separates them when collapsed. */
    private val navGroups = listOf(
        "Overview" to listOf("dashboard", "notifications"),
        "Support" to listOf("issues", "tickets", "kanban"),
        "Field work" to listOf("meetings", "visits", "checklists"),
        "Team" to listOf("chat", "portals", "more"),
    )
    var page = "dashboard"
    /** The asset to open once signed in, from an /asset/<id> link (written on each asset's NFC tag). */
    private var openAsset = ""
    var currentName = "Support user"
    var currentRole = "user"
    var currentContactEmail = ""
        private set
    private var assistantOpen = false
    private var assistantWaiting = false
    private var assistantRequestId = 0
    private var otpChallenge = ""
    private var otpDestination = ""
    private var completingOtp = false

    fun start() {
        MobileInsights.install()
        if (MobileInsights.enabled) page = MobileInsights.kind
        Regex("^/asset/([A-Za-z0-9]{1,40})/?$").find(window.location.pathname)?.let { page = "more"; openAsset = it.groupValues[1] }
        applyTheme(window.localStorage.getItem("support-theme") ?: "system")
        FirebaseGateway.observeAuth { user ->
            revealApp()
            if (!WEB_OTP_ENABLED) {
                if (user == null) { if (MobileInsights.enabled) MobileInsights.waiting() else showLogin() } else initializeSession(user)
            } else if (user == null) {
                if (otpChallenge.isBlank()) showLogin() else showOtp()
            } else {
                val verifiedUid = window.sessionStorage.getItem("zi-web-otp-verified") ?: ""
                if (completingOtp || verifiedUid == (user.uid as? String ?: "")) initializeSession(user) else requestOtp()
            }
        }
    }

    private fun showLogin() {
        element("app")?.innerHTML = """
          <main class="login-page">
            <section class="login-brand">
              <div class="login-mark"><img class="brand-logo login-logo" src="/zora_innovations_logo.png" alt="Zora Innovations"><span>Support Operations</span></div><h1>Welcome back.</h1>
              <p>Sign in to pick up your tickets, incidents and site visits.</p>
              <div class="trust"><span><i class="material-symbols-rounded">verified_user</i> Two-step verification</span><span><i class="material-symbols-rounded">lock</i> Private to the support team</span></div>
              <div class="login-disc" aria-hidden="true"><span class="material-symbols-rounded">verified_user</span></div>
            </section>
            <section class="login-panel"><form id="login-form" class="login-card" novalidate>
              <div class="login-kicker">Zora Innovations</div><h2>Sign in</h2><p>Use your support username and password.</p>
              <div class="field"><label for="login-user">Username</label><div class="login-input"><span class="material-symbols-rounded" aria-hidden="true">person</span><input class="input" id="login-user" autocomplete="username" required></div></div>
              <div class="field"><label for="login-password">Password</label><div class="login-input secret-field"><span class="material-symbols-rounded" aria-hidden="true">lock</span><input class="input" id="login-password" type="password" autocomplete="current-password" required><button id="toggle-login-password" class="btn btn-icon btn-ghost" type="button" aria-label="Show password" title="Show password"><span class="material-symbols-rounded" aria-hidden="true">visibility</span></button></div></div>
              <button id="login-button" class="btn btn-primary btn-block login-submit" type="submit">Sign in</button>
              <p id="login-error" style="color:var(--danger);margin-top:14px"></p>
              
            </form></section>
          </main>
        """.trimIndent()
        document.getElementById("login-form")?.addEventListener("submit", { event ->
            event.preventDefault()
            val username = input("login-user")
            val password = input("login-password")
            if (username.isBlank() || password.isBlank()) return@addEventListener
            element("login-button")?.textContent = "Signing in…"
            promise(FirebaseGateway.login(username, password), {}, { message ->
                element("login-button")?.textContent = "Sign in"
                element("login-error")?.textContent = when {
                    "credential" in message.lowercase() || "password" in message.lowercase() -> "Incorrect username or password."
                    else -> message
                }
            })
        })
        click("toggle-login-password") {
            val password = document.getElementById("login-password") as? HTMLInputElement ?: return@click
            val reveal = password.type == "password"
            password.type = if (reveal) "text" else "password"
            val button = element("toggle-login-password")
            button?.setAttribute("aria-label", if (reveal) "Hide password" else "Show password")
            button?.setAttribute("title", if (reveal) "Hide password" else "Show password")
            button?.innerHTML = "<span class=\"material-symbols-rounded\" aria-hidden=\"true\">${if (reveal) "visibility_off" else "visibility"}</span>"
            password.focus()
        }
    }

    private fun requestOtp() {
        element("app")?.innerHTML = "<main class=\"boot\"><div class=\"spinner\"></div><h2>Sending verification code</h2><p>Securing your web session…</p></main>"
        promise(FirebaseGateway.callable("requestWebLoginOtp"), { result ->
            otpChallenge = field(result.data, "challengeId")
            otpDestination = field(result.data, "maskedEmail")
            FirebaseGateway.logout()
        }, { message ->
            FirebaseGateway.logout(); otpChallenge = ""; showLogin(); element("login-error")?.textContent = message
        })
    }

    private fun showOtp() {
        element("app")?.innerHTML = """
          <main class="login-page">
            <section class="login-brand"><div class="login-mark"><img class="brand-logo login-logo" src="/zora_innovations_logo.png" alt="Zora Innovations"><span>Support Operations</span></div><h1>One more step.</h1><p>We verify every web sign-in to protect your support records and private team conversations.</p><div class="login-disc" aria-hidden="true"><span class="material-symbols-rounded">shield_lock</span></div></section>
            <section class="login-panel"><form id="otp-form" class="login-card" novalidate>
              <div class="login-kicker">Two-step verification</div><h2>Check your email</h2><p>We sent a four-digit verification code to <strong>${escape(otpDestination)}</strong>. It expires in 10 minutes.</p>
              <div class="field"><label for="otp-code">Verification code</label><input class="input otp-code" id="otp-code" inputmode="numeric" autocomplete="one-time-code" maxlength="4" pattern="[0-9]{4}" placeholder="0000" required></div>
              <button id="otp-button" class="btn btn-primary btn-block login-submit" type="submit">Verify and continue</button>
              <button id="otp-back" class="btn btn-ghost btn-block" type="button">Back to sign in</button>
              <p id="otp-error" style="color:var(--danger);margin-top:14px"></p>
            </form></section>
          </main>
        """.trimIndent()
        document.getElementById("otp-form")?.addEventListener("submit", { event -> event.preventDefault(); verifyOtp() })
        type("otp-code") { val node=document.getElementById("otp-code") as? HTMLInputElement;node?.value=node?.value?.filter { it.isDigit() }?.take(4).orEmpty() }
        click("otp-back") { otpChallenge="";otpDestination="";FirebaseGateway.logout();showLogin() }
    }

    private fun verifyOtp() {
        val code=input("otp-code")
        if(code.length!=4){element("otp-error")?.textContent="Enter the four-digit code.";return}
        (element("otp-button") as? org.w3c.dom.HTMLButtonElement)?.disabled=true;element("otp-button")?.textContent="Verifying…"
        promise(FirebaseGateway.callable("verifyWebLoginOtp",json("challengeId" to otpChallenge,"code" to code)),{result->
            val token=field(result.data,"customToken");completingOtp=true
            promise(FirebaseGateway.loginWithCustomToken(token),{userResult->val uid=userResult.user.uid as String;window.sessionStorage.setItem("zi-web-otp-verified",uid);otpChallenge="";otpDestination=""},{message->completingOtp=false;showOtp();element("otp-error")?.textContent=message})
        },{message->(element("otp-button") as? org.w3c.dom.HTMLButtonElement)?.disabled=false;element("otp-button")?.textContent="Verify and continue";element("otp-error")?.textContent=message})
    }

    private fun initializeSession(user: dynamic, attempt: Int = 0) {
        val email = user.email as? String ?: ""
        // Only @supportapp.internal accounts pass the Firestore rules. Anything else (a
        // personal email typed/autofilled into Username) would open a broken "Support user" shell.
        if (!email.lowercase().endsWith("@supportapp.internal")) {
            FirebaseGateway.logout()
            showLogin(); element("login-error")?.textContent = "Sign in with your support username, not an email address."
            return
        }
        loadSavedCompanies()
        val fallback = email.substringBefore("@").replaceFirstChar { it.uppercase() }
        promise(FirebaseGateway.getDocument("supportUsers/$email"), { snapshot ->
            currentName = if (snapshot.exists()) field(snapshot.data(), "username").ifBlank { fallback } else fallback
            currentRole = if (snapshot.exists()) field(snapshot.data(), "role").ifBlank { "user" } else "user"
            // Their real email; the login ID ($email) is the document ID only and never shown.
            currentContactEmail = if (!snapshot.exists()) "" else com.zi.support.shared.SupportLogin.realEmail(field(snapshot.data(), "email")).ifBlank { field(snapshot.data(), "webEmail") }
            FirebaseGateway.set("supportUsers/$email", json(
                "uid" to user.uid, "username" to currentName,
                "lastLoginAt" to FirebaseGateway.serverTimestamp(), "online" to true,
                "lastSeenAt" to FirebaseGateway.serverTimestamp()
            ), merge = true)
            showShell(email)
        }, { message ->
            // Never fall through to the shell here: it would run as "Support user" with the
            // Member role. Retry once with a fresh ID token, then offer a retry (no logout).
            console.error("Support profile load failed (attempt ${attempt + 1}):", message)
            if (attempt == 0) {
                user.getIdToken(true).then({ initializeSession(user, 1) }, { initializeSession(user, 1) })
                return@promise
            }
            element("app")?.innerHTML = """
              <main class="boot"><h2>We couldn't load your profile</h2><p>${escape(message)}</p>
              <button id="profile-retry" class="btn btn-primary"><span class="material-symbols-rounded">refresh</span> Try again</button>
              <button id="profile-logout" class="btn btn-ghost">Log out</button></main>
            """.trimIndent()
            click("profile-retry") { element("app")?.innerHTML = "<main class=\"boot\"><div class=\"spinner\"></div></main>"; initializeSession(user, 0) }
            click("profile-logout") { FirebaseGateway.logout() }
        })
    }

    private fun showShell(email: String) {
        element("app")?.innerHTML = """
          <div class="app-shell${if (navCollapsed()) " nav-collapsed" else ""}">
            <aside class="sidebar">
              <div class="side-brand sb-brand"><span class="sb-mark"><img src="/zora_innovations_logo.png" alt="Zora Innovations"></span><span class="sb-name"><strong>Support Portal</strong><small>Zora Innovations</small></span></div>
              <nav class="nav-list" aria-label="Main">${navGroups.joinToString("") { (label, ids) -> "<div class=\"nav-section-label\">$label</div>" + sidebarPages.filter { it.id in ids }.joinToString("") { "<button class=\"nav-item\" data-page=\"${it.id}\" data-label=\"${it.title}\" aria-label=\"${it.title}\"><span class=\"nav-icon material-symbols-rounded\" aria-hidden=\"true\">${it.icon}</span><span class=\"nav-label\">${it.title}</span></button>" } }}</nav>
              <button id="nav-collapse" class="nav-item nav-collapse" type="button"></button>
              <div class="side-user"><div class="user-avatar">${escape(currentName.take(1))}</div><div class="user-copy"><strong>${escape(currentName)}</strong><small>${escape(currentContactEmail.ifBlank { com.zi.support.shared.SupportRoles.label(currentRole) })}</small></div></div>
            </aside>
            <main class="main">
              <header class="topbar"><div class="page-head"><span id="page-icon" class="page-icon material-symbols-rounded" aria-hidden="true"></span><div><h1 id="page-title"></h1><p id="page-subtitle"></p></div></div>
                <div class="top-actions hb">
                  <button id="report-button" class="hb-report" title="Generate report"><span class="material-symbols-rounded" aria-hidden="true">summarize</span><span class="hb-text">Generate report</span></button>
                  <div class="hb-group">
                    <button id="calls-button" class="hb-icon calls-button" title="Calls" aria-label="Calls"><span class="material-symbols-rounded" aria-hidden="true">call</span><span id="calls-badge" class="calls-badge" aria-hidden="true"></span></button>
                    <button id="refresh" class="hb-icon" title="Refresh" aria-label="Refresh"><span class="material-symbols-rounded" aria-hidden="true">refresh</span></button>
                    <button id="settings" class="hb-icon" title="Settings" aria-label="Settings"><span class="material-symbols-rounded" aria-hidden="true">settings</span></button>
                    <span class="hb-divider" aria-hidden="true"></span>
                    <button id="logout" class="hb-icon hb-logout" title="Log out" aria-label="Log out"><span class="material-symbols-rounded" aria-hidden="true">logout</span></button>
                  </div>
                </div>
              </header>
              <div id="page-loading" class="page-spinner" role="status" aria-label="Loading" style="display:none"></div>
              <section id="content" class="content"></section>
            </main>
          </div>
          <button id="assistant-fab" class="assistant-fab" aria-label="Open ZI Assistant" title="ZI Assistant"><img src="/zora_innovations_logo.png" alt="ZI Assistant"></button>
        """.trimIndent()
        document.querySelectorAll("[data-page]").let { nodes ->
            for (i in 0 until nodes.length) nodes.item(i)?.addEventListener("click", { e ->
                val target = e.currentTarget as HTMLElement
                navigate(target.getAttribute("data-page") ?: "issues")
            })
        }
        paintNavCollapse()
        click("nav-collapse") {
            val collapsed = document.querySelector(".app-shell")?.classList?.toggle("nav-collapsed") == true
            try { window.localStorage.setItem("support-nav-collapsed", collapsed.toString()) } catch (_: Throwable) {}
            paintNavCollapse()
        }
        click("calls-button") { navigate("calls") }
        click("report-button") { TicketsScreen.openReport() }
        CallsScreen.watchBadge()
        click("refresh") {
            val button = element("refresh")
            button?.classList?.add("spinning")
            FirebaseGateway.reconnect().then {
                // Tickets: read from the server so Refresh really fetches, and say whether it worked.
                when (page) {
                    "tickets" -> TicketsScreen.refreshFromServer { button?.classList?.remove("spinning") }
                    "meetings" -> MeetingsScreen.refreshFromServer { button?.classList?.remove("spinning") }
                    else -> { button?.classList?.remove("spinning"); render() }
                }
            }
        }
        // Back on the tab after a while: restart the connection so the open page catches up with what changed meanwhile.
        if (!watchingVisibility) {
            watchingVisibility = true
            document.addEventListener("visibilitychange", {
                if (document.asDynamic().visibilityState == "hidden") hiddenAt = kotlin.js.Date.now()
                else if (hiddenAt > 0 && kotlin.js.Date.now() - hiddenAt > 60_000) { hiddenAt = 0.0; FirebaseGateway.reconnect() }
            })
        }
        click("settings") { navigate("settings") }
        click("logout") { confirmLogout() }
        click("assistant-fab") { toggleAssistant() }
        navigate(page)
    }

    private var watchingVisibility = false
    private var hiddenAt = 0.0

    private fun navCollapsed(): Boolean = try { window.localStorage.getItem("support-nav-collapsed") == "true" } catch (_: Throwable) { false }

    private fun paintNavCollapse() {
        val collapsed = document.querySelector(".app-shell")?.classList?.contains("nav-collapsed") == true
        val label = if (collapsed) "Expand menu" else "Collapse menu"
        element("nav-collapse")?.apply {
            innerHTML = "<span class=\"nav-icon material-symbols-rounded\">${if (collapsed) "left_panel_open" else "left_panel_close"}</span><span class=\"nav-label\">$label</span>"
            setAttribute("aria-label", label); setAttribute("title", label); setAttribute("aria-expanded", (!collapsed).toString())
        }
    }

    fun navigate(target: String) {
        page = if (pages.any { it.id == target }) target else "dashboard"
        render()
    }

    private fun render() {
        IssuesScreen.stop()
        TicketsScreen.stop()
        MeetingsScreen.stop()
        KanbanScreen.stop()
        CallsScreen.stop()
        RecordsScreen.stop()
        ChatScreen.stop()
        DashboardScreen.stop()
        stopPortals()
        val selected = pages.firstOrNull { it.id == page } ?: pages.first()
        document.title = "${selected.title} | SupportApp"
        element("page-title")?.textContent = selected.title
        element("page-icon")?.textContent = selected.icon
        element("page-subtitle")?.textContent = selected.subtitle
        document.querySelectorAll("[data-page]").let { nodes -> for (i in 0 until nodes.length) {
            val node = nodes.item(i) as HTMLElement
            node.classList.toggle("active", node.getAttribute("data-page") == page)
        } }
        element("content")?.innerHTML = "<div class=\"empty\">Loading ${escape(selected.title.lowercase())}…</div>"
        try {
            when (page) {
                "dashboard" -> DashboardScreen.renderDashboard()
                "issues" -> IssuesScreen.render()
                "tickets" -> TicketsScreen.render()
                "kanban" -> KanbanScreen.render()
                "meetings" -> MeetingsScreen.render()
                "notifications" -> DashboardScreen.renderNotifications()
                "checklists" -> RecordsScreen.renderChecklists()
                "visits" -> RecordsScreen.renderVisits()
                "chat" -> ChatScreen.render()
                "portals" -> showPortals()
                "settings" -> renderSettings()
                "more" -> if (openAsset.isEmpty()) renderMore() else {
                    val id = openAsset
                    openAsset = ""
                    window.history.replaceState(null, "", "/")
                    AssetRegistryScreen.render(id)
                }
                "calls" -> CallsScreen.render()
                "api-health" -> ApiHealthScreen.render()
            }
        } catch (error: Throwable) {
            element("content")?.innerHTML = "<div class=\"empty\">This page could not be loaded. Please refresh and try again.</div>"
            toast(error.message ?: "This page could not be loaded.", true)
        }
    }

    private fun confirmLogout() {
        confirmModal("Log out?", "You’ll need your username and password to sign in again.", "confirm-logout", "Log out", icon = "logout")
        click("confirm-logout") {
            val email = FirebaseGateway.currentUser()?.email as? String
            // Wait for the "offline" write so it isn't left queued against a signed-out session.
            val offline: dynamic = if (email.isNullOrBlank()) kotlin.js.Promise.resolve(null)
                else FirebaseGateway.update("supportUsers/$email", json("online" to false, "lastSeenAt" to FirebaseGateway.serverTimestamp()))
            window.sessionStorage.removeItem("zi-web-otp-verified");completingOtp=false;otpChallenge="";otpDestination="";closeModal()
            // Stop every screen's listeners before signing out so none of them error on the
            // signed-out session, and forget the previous identity so it can never leak into the next login.
            IssuesScreen.stop(); TicketsScreen.stop(); MeetingsScreen.stop(); KanbanScreen.stop(); RecordsScreen.stop(); ChatScreen.stop(); DashboardScreen.stop(); stopPortals()
            currentName = "Support user"; currentRole = "user"; currentContactEmail = ""; page = "dashboard"
            offline.then({ FirebaseGateway.logout() }, { FirebaseGateway.logout() })
        }
    }

    private fun renderSettings() {
        val email = FirebaseGateway.currentUser()?.email as? String ?: ""
        val admin = com.zi.support.shared.SupportRoles.isAdmin(currentRole)
        val initials = currentName.split(" ").filter { it.isNotBlank() }.take(2).joinToString("") { it.take(1) }.uppercase()
        // Each theme card shows a tiny window in that theme; "system" is split down the middle.
        fun themeButton(value: String, icon: String, label: String) =
            "<button type=\"button\" class=\"sx-theme\" data-theme-choice=\"$value\"><span class=\"st-preview st-preview-$value\" aria-hidden=\"true\"><i></i><b></b><b></b></span><span class=\"sx-theme-label\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">$icon</span>$label</span></button>"
        // Settings: a header, then sections with their title on the left and controls on the right; the directory is full width.
        element("content")?.innerHTML = """
          <div class="sx">
            <section class="sx-section sx-first">
              <div class="sx-section-intro"><h3>Profile</h3><p>Signed in to the support portal.</p></div>
              <div class="sx-card sx-profile">
                <span class="sx-avatar">${escape(initials.ifBlank { "#" })}</span>
                <div class="sx-who"><h4>${escape(currentName)}</h4>
                  <p><span class="material-symbols-rounded" aria-hidden="true">mail</span>${escape(currentContactEmail.ifBlank { "No verification email" })}</p>
                  <span class="sx-role ${if (admin) "is-admin" else ""}"><span class="material-symbols-rounded" aria-hidden="true">${if (admin) "shield_person" else "person"}</span>${if (admin) "Admin · manages users, customers and domains" else "Member"}</span></div>
                <button id="settings-logout" class="btn sx-logout"><span class="material-symbols-rounded" aria-hidden="true">logout</span>Log out</button>
              </div>
            </section>
            <section class="sx-section">
              <div class="sx-section-intro"><h3>Appearance</h3><p>System follows your device's light or dark setting.</p></div>
              <div class="sx-card"><div class="sx-themes" role="radiogroup" aria-label="Theme">${themeButton("system","brightness_auto","System")}${themeButton("light","light_mode","Light")}${themeButton("dark","dark_mode","Dark")}</div></div>
            </section>
            <section class="sx-section sx-section-wide">
              <div class="sx-section-intro"><h3>Directory</h3><p>Support users, customers, allowed domains and email history.</p></div>
              <div class="sx-card sx-directory"><div id="settings-directory" class="settings-directory"></div></div>
            </section>
          </div>
        """.trimIndent()
        val theme = window.localStorage.getItem("support-theme") ?: "system"
        document.querySelectorAll("[data-theme-choice]").let { nodes -> for (i in 0 until nodes.length) {
            val node = nodes.item(i) as org.w3c.dom.HTMLElement
            val value = node.getAttribute("data-theme-choice") ?: continue
            node.setAttribute("aria-checked", (value == theme).toString()); node.setAttribute("role", "radio")
            if (value == theme) node.classList.add("active")
            node.addEventListener("click", {
                for (j in 0 until nodes.length) { val other = nodes.item(j) as org.w3c.dom.HTMLElement; other.classList.remove("active"); other.setAttribute("aria-checked", "false") }
                node.classList.add("active"); node.setAttribute("aria-checked", "true")
                applyTheme(value); window.localStorage.setItem("support-theme", value)
            })
        } }
        click("settings-logout") { confirmLogout() }
        SettingsDirectoryView.render("settings-directory")
    }

    private fun renderMore() {
        element("content")?.innerHTML = """
          <div class="page-intro"><div><span class="eyebrow">Tools</span><h2>More</h2><p>Connectivity, access codes and project tools.</p></div></div>
          <div class="service-grid more-service-grid">
            <button class="service-card compact-service-card" id="more-servers" hidden><span class="service-icon material-symbols-rounded">dns</span><strong>Servers</strong></button>
            <button class="service-card compact-service-card" id="more-api-health"><span class="service-icon material-symbols-rounded">network_check</span><strong>API</strong></button>
            <button class="service-card compact-service-card" id="more-access-codes"><span class="service-icon material-symbols-rounded">key</span><strong>Access codes</strong></button>
            <button class="service-card compact-service-card" id="more-templates"><span class="service-icon material-symbols-rounded">description</span><strong>Templates</strong></button>
            <button class="service-card compact-service-card" id="more-devices"><span class="service-icon material-symbols-rounded">devices</span><strong>Devices</strong></button>
            <button class="service-card compact-service-card" id="more-driver-logs"><span class="service-icon material-symbols-rounded">local_shipping</span><strong>Driver app logs</strong></button>
            <button class="service-card compact-service-card" id="more-locations"><span class="service-icon material-symbols-rounded">location_on</span><strong>Locations</strong></button>
            <button class="service-card compact-service-card" id="more-assets"><span class="service-icon material-symbols-rounded">inventory_2</span><strong>Asset registry</strong></button>
            <button class="service-card compact-service-card" id="more-guides"><span class="service-icon material-symbols-rounded">menu_book</span><strong>User guides</strong></button>
            <button class="service-card compact-service-card" id="more-portal-usage"><span class="service-icon material-symbols-rounded">monitoring</span><strong>Portal usage</strong></button>
            <button class="service-card compact-service-card" id="more-leaderboard"><span class="service-icon material-symbols-rounded">emoji_events</span><strong>Leaderboard</strong></button>
          </div>
          <p class="release-label">${escape(ReleaseInfo.label)} · ${escape(ReleaseInfo.BUILD_DATE)}</p>
        """.trimIndent()
        click("more-servers") { showServers() }
        click("more-api-health") { ApiHealthScreen.render() }
        click("more-access-codes") { AccessCodesScreen.render() }
        click("more-templates") { TemplatesScreen.render() }
        click("more-leaderboard") { LeaderboardScreen.render() }
        click("more-portal-usage") { PortalUsageScreen.render() }
        click("more-devices") { DeviceManagementScreen.show() }
        click("more-driver-logs") { DriverLogsScreen.render() }
        click("more-locations") { LocationsScreen.render() }
        click("more-assets") { AssetRegistryScreen.render() }
        click("more-guides") { GuidesScreen.render() }
    }

    /** Every portal link, by Firestore id, so Edit can prefill the form. */
    private var addedPortals = mapOf<String, Pair<String, com.zi.support.shared.Portal>>()

    /**
     * Portals page: each company's portal links (Firestore, grouped by PortalDirectory); links open in a new tab and
     * every one can be edited or deleted.
     */
    private var portalsStop: dynamic = null

    /**
     * Live listener, not a one-off read: Firestore shows our own add/edit/delete in it immediately (before the server
     * confirms), so saving never waits on the network.
     */
    private fun showPortals() {
        stopPortals()
        val directory = com.zi.support.shared.PortalDirectory
        portalsStop = FirebaseGateway.listenCollection(directory.COLLECTION, onRows = { rows ->
            val links = rows.mapNotNull { row ->
                val company = row.data.company as? String ?: return@mapNotNull null
                val url = row.data.url as? String ?: return@mapNotNull null
                company to com.zi.support.shared.Portal(row.data.name as? String ?: directory.nameFor(url), url, row.id)
            }
            addedPortals = links.associateBy { it.second.id!! }
            renderPortals(directory.grouped(links))
        }, onError = { error -> toast("Couldn't load portals: $error", true) })
    }

    private fun stopPortals() { cancelListener(portalsStop); portalsStop = null }

    /** The company to show expanded after a save, as well as any the user already had open. */
    private var portalsFocus: String? = null

    private fun renderPortals(customers: List<com.zi.support.shared.CustomerPortals>) {
        if (page != "portals") return stopPortals()
        val open = mutableSetOf<String>()
        val openNodes = document.querySelectorAll(".portal-group[open]")
        for (i in 0 until openNodes.length) (openNodes.item(i) as HTMLElement).getAttribute("data-company")?.let { open += it.lowercase() }
        portalsFocus?.let { open += it.lowercase() }
        portalsFocus = null
        var index = 0
        val body = "<div class=\"toolbar\"><p class=\"portal-intro\">Choose a company to see its portals. Each opens in a new tab.</p>" +
            "<button id=\"portal-add\" class=\"btn btn-primary\" type=\"button\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">add_link</span>Add link</button></div>" +
            "<div class=\"portal-list\">" +
            customers.joinToString("") { customer ->
                val n = customer.portals.size
                index = 0 // stagger restarts per company, since each opens on its own
                val count = if (n == 0) "Coming soon" else "$n portal${if (n == 1) "" else "s"}"
                val links = if (n == 0) "<p class=\"portal-empty\">Links coming soon</p>"
                    else "<div class=\"portal-grid\">" + customer.portals.joinToString("") { portal ->
                        val host = portal.url.substringAfter("://").substringBefore('/')
                        val id = portal.id
                        "<div class=\"portal-item${if (id != null) " editable" else ""}\" style=\"--i:${index++}\">" +
                            "<a class=\"portal-card tilt\" href=\"${escape(portal.url)}\" target=\"_blank\" rel=\"noopener\" title=\"${escape(portal.name)} · ${escape(portal.url)}\">" +
                            "<span class=\"portal-icon material-symbols-rounded\" aria-hidden=\"true\">language</span>" +
                            "<span class=\"portal-text\"><strong>${escape(portal.name)}</strong><small>${escape(host)}</small></span>" +
                            "<span class=\"portal-open material-symbols-rounded\" aria-hidden=\"true\">arrow_outward</span></a>" +
                            (if (id == null) "" else "<div class=\"portal-actions\">" +
                                "<button class=\"btn btn-icon btn-ghost\" type=\"button\" data-portal-edit=\"${escape(id)}\" title=\"Edit\" aria-label=\"Edit ${escape(portal.name)}\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">edit</span></button>" +
                                "<button class=\"btn btn-icon btn-ghost\" type=\"button\" data-portal-delete=\"${escape(id)}\" title=\"Delete\" aria-label=\"Delete ${escape(portal.name)}\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">delete</span></button></div>") +
                            "</div>"
                    } + "</div>"
                "<details class=\"portal-group\" data-company=\"${escape(customer.customer)}\"${if (customer.customer.lowercase() in open) " open" else ""}><summary class=\"portal-customer\">" +
                    "<span class=\"portal-badge\" aria-hidden=\"true\">${escape(customer.customer.take(2).uppercase())}</span>" +
                    "<span class=\"portal-name\">${escape(customer.customer)}</span><small>$count</small>" +
                    "<span class=\"portal-chevron material-symbols-rounded\" aria-hidden=\"true\">expand_more</span></summary>" +
                    links + "</details>"
            } + (if (customers.isEmpty()) "<div class=\"empty\">No portals yet. Use Add link to add one.</div>" else "") + "</div>"
        element("content")?.innerHTML = body
        enableTilt(".portal-card.tilt")
        click("portal-add") { showPortalForm(null) }
        val edits = document.querySelectorAll("[data-portal-edit]")
        for (i in 0 until edits.length) (edits.item(i) as HTMLElement).let { button ->
            button.addEventListener("click", { showPortalForm(button.getAttribute("data-portal-edit")) })
        }
        val deletes = document.querySelectorAll("[data-portal-delete]")
        for (i in 0 until deletes.length) (deletes.item(i) as HTMLElement).let { button ->
            button.addEventListener("click", { confirmDeletePortal(button.getAttribute("data-portal-delete") ?: "") })
        }
    }

    /** Add (editId null) or edit an added portal link: company, name and link. */
    private fun showPortalForm(editId: String?) {
        val directory = com.zi.support.shared.PortalDirectory
        val existing = editId?.let { addedPortals[it] }
        if (editId != null && existing == null) { toast("That link no longer exists", true); return }
        val companies = (directory.companies + listOfNotNull(existing?.first)).distinctBy { it.lowercase() }.sortedBy { it.lowercase() }
        modal(if (existing == null) "Add portal link" else "Edit portal link", """
            <div class="form-grid">
              <div class="field span-2"><label for="portal-company">Company</label><div class="select-shell"><span class="material-symbols-rounded" aria-hidden="true">apartment</span><select id="portal-company"><option value="">Choose company…</option>${companies.joinToString("") { "<option${if (it == existing?.first) " selected" else ""}>${escape(it)}</option>" }}</select></div></div>
              <div class="field span-2"><label for="portal-name">Portal name</label><input id="portal-name" class="input" maxlength="200" placeholder="e.g. Ops dashboard" autocomplete="off" value="${escape(existing?.second?.name ?: "")}"></div>
              <div class="field span-2"><label for="portal-url">Link</label><input id="portal-url" class="input" type="url" maxlength="500" placeholder="https://…" autocomplete="off" value="${escape(existing?.second?.url ?: "")}"></div>
            </div>
        """.trimIndent(), "<button id=\"portal-save\" class=\"btn btn-primary\" type=\"button\">${if (existing == null) "Add" else "Save"}</button>")
        val company = document.getElementById("portal-company").asDynamic()
        val nameInput = document.getElementById("portal-name") as HTMLInputElement
        val urlInput = document.getElementById("portal-url") as HTMLInputElement
        click("portal-save") {
            val chosen = company.value as String
            val name = nameInput.value.trim()
            val url = directory.normalizeUrl(urlInput.value)
            when {
                chosen.isBlank() -> toast("Choose a company", true)
                name.isEmpty() -> toast("Enter a portal name", true)
                url == null -> toast("Paste a valid link, e.g. https://example.com", true)
                else -> {
                    val email = FirebaseGateway.currentUser()?.email
                    val write = if (editId == null) FirebaseGateway.add(directory.COLLECTION, json(
                        "company" to chosen, "name" to name, "url" to url,
                        "createdBy" to email, "createdAt" to FirebaseGateway.serverTimestamp()
                    )) else FirebaseGateway.update("${directory.COLLECTION}/$editId", json(
                        "company" to chosen, "name" to name, "url" to url,
                        "updatedBy" to email, "updatedAt" to FirebaseGateway.serverTimestamp()
                    ))
                    // Don't wait for the server: the listener already shows the change. A rejected write is undone
                    // there automatically, and reported here.
                    portalsFocus = chosen
                    closeModal()
                    toast(if (editId == null) "Link added to $chosen" else "Link updated")
                    promise(write, { }, { error -> toast("Couldn't save the link: $error", true) })
                }
            }
        }
        (if (existing == null) company else nameInput).unsafeCast<HTMLElement>().focus()
    }

    private fun confirmDeletePortal(id: String) {
        val (company, portal) = addedPortals[id] ?: return
        confirmModal("Delete ${portal.name}?", "It will be removed from $company for everyone.", "confirm-portal-delete", "Delete")
        click("confirm-portal-delete") {
            portalsFocus = company
            closeModal()
            toast("Link deleted")
            promise(FirebaseGateway.delete("${com.zi.support.shared.PortalDirectory.COLLECTION}/$id"), { }, { error -> toast("Couldn't delete the link: $error", true) })
        }
    }

    private fun showServers() {
        val servers = listOf(
            "Gantrans" to "https://gantrans.net.za:56308",
            "KPH" to "https://kphlogistics.net.za:56308",
            "Transport" to "https://transportdbn.net.za:56308",
            "Delta" to "https://deltaconsol.net.za:56308",
            "Texas" to "https://texastrucking.net.za:56308"
        )
        val body = "<div class=\"server-directory\">" + servers.joinToString("") { (name,url) ->
            "<a class=\"server-row\" href=\"${escape(url)}\" target=\"_blank\" rel=\"noopener\"><span class=\"service-icon material-symbols-rounded\">dns</span><span><strong>${escape(name)}</strong><small>${escape(url)}</small></span><span class=\"material-symbols-rounded\">open_in_new</span></a>"
        } + "</div>"
        modal("Servers", body, wide = true)
    }

    private fun toggleAssistant() {
        if (assistantOpen) { stopAssistantResponse(); saveAssistantHistory(); element("assistant-window")?.remove(); assistantOpen = false; return }
        assistantOpen = true
        val node = document.createElement("section") as HTMLElement
        node.id = "assistant-window"; node.className = "assistant-window"
        val historyHtml = loadAssistantHistory().ifBlank { assistantGreetingHtml() }
        node.innerHTML = """
          <header class="assistant-head"><div><strong>ZI Assistant</strong><small style="display:block;opacity:.75">Support workspace intelligence</small></div><button id="assistant-close" class="btn btn-icon btn-ghost" style="color:white"><span class="material-symbols-rounded">close</span></button></header>
          <div class="assistant-prompts"><button data-prompt="Summarise today's open incidents">Today’s incidents</button><button data-prompt="Show my assigned tickets">My tickets</button><button data-prompt="How does the chat tab work?">App help</button><button data-prompt="Summarise today's completed work">Daily summary</button></div>
          <div id="assistant-messages" class="assistant-messages">$historyHtml</div>
          <div class="composer"><input id="assistant-input" class="input" placeholder="Ask about your support workspace…"><button id="assistant-send" class="btn btn-primary btn-icon" title="Send"><span class="material-symbols-rounded">send</span></button></div>
        """.trimIndent()
        document.body?.appendChild(node)
        click("assistant-close") { toggleAssistant() }
        click("assistant-send") { askAssistant() }
        document.querySelectorAll("[data-prompt]").let { nodes -> for (i in 0 until nodes.length) nodes.item(i)?.addEventListener("click", { event ->
            (document.getElementById("assistant-input") as? HTMLInputElement)?.value = (event.currentTarget as HTMLElement).getAttribute("data-prompt").orEmpty(); askAssistant()
        }) }
        document.getElementById("assistant-input")?.addEventListener("keydown", { e -> if ((e as KeyboardEvent).key == "Enter") askAssistant() })
        bindAssistantConfirmationButtons(element("assistant-messages") as HTMLElement)
        element("assistant-messages")?.let { it.scrollTop = it.scrollHeight.toDouble() }
    }

    private fun askAssistant() {
        if (assistantWaiting) { stopAssistantResponse(); return }
        val question = input("assistant-input")
        if (question.isBlank()) return
        val requestId = ++assistantRequestId
        (document.getElementById("assistant-input") as? HTMLInputElement)?.value = ""
        val messages = element("assistant-messages") ?: return
        messages.innerHTML += "<div class=\"assistant-message mine\">${escape(question)}</div><div id=\"assistant-thinking\" class=\"assistant-message\">Thinking…</div>"
        messages.scrollTop = messages.scrollHeight.toDouble()
        setAssistantWaiting(true)
        saveAssistantHistory(messages)
        promise(FirebaseGateway.callable("askSupportAssistant", json("question" to question)), { result ->
            if (requestId != assistantRequestId || !assistantWaiting) return@promise
            setAssistantWaiting(false)
            element("assistant-thinking")?.remove()
            appendAssistantResult(messages, result.data)
        }, { message ->
            if (requestId != assistantRequestId || !assistantWaiting) return@promise
            setAssistantWaiting(false)
            element("assistant-thinking")?.textContent = message
            saveAssistantHistory(messages)
        })
    }

    private fun appendAssistantResult(messages: HTMLElement, data: dynamic) {
        val answer = field(data, "answer").ifBlank { field(data, "text") }.ifBlank { "I couldn’t produce an answer." }
        messages.innerHTML += "<div class=\"assistant-message assistant-rich\">${assistantReplyHtml(answer)}${assistantConfirmationHtml(data.confirmation)}</div>"
        messages.scrollTop = messages.scrollHeight.toDouble()
        bindAssistantConfirmationButtons(messages)
        saveAssistantHistory(messages)
    }

    private fun assistantConfirmationHtml(data: dynamic): String {
        val id = field(data, "id")
        if (id.isBlank()) return ""
        val status = field(data, "status").ifBlank { "pending" }
        val items = stringList(data.items).take(8).joinToString("") { "<li>${escape(it)}</li>" }
        val details = """
            <div class="assistant-confirmation" data-pending-id="${escape(id)}">
              ${if (field(data, "title").isBlank()) "" else "<strong>${escape(field(data, "title"))}</strong>"}
              ${if (field(data, "summary").isBlank()) "" else "<p>${escape(field(data, "summary"))}</p>"}
              ${if (items.isBlank()) "" else "<ul>$items</ul>"}
              ${if (status == "pending") "<div class=\"assistant-confirm-actions\"><button class=\"btn btn-primary assistant-confirm\" data-decision=\"confirm\">Confirm</button><button class=\"btn assistant-confirm\" data-decision=\"cancel\">Cancel</button></div>" else "<small>Status: ${escape(status.replaceFirstChar { it.uppercase() })}</small>"}
            </div>
        """.trimIndent()
        return details
    }

    private fun bindAssistantConfirmationButtons(messages: HTMLElement) {
        val buttons = document.querySelectorAll(".assistant-confirm")
        for (i in 0 until buttons.length) {
            val button = buttons.item(i) as? HTMLElement ?: continue
            if (button.getAttribute("data-bound") == "true") continue
            button.setAttribute("data-bound", "true")
            button.addEventListener("click", { event ->
                val target = event.currentTarget as HTMLElement
                val card = target.closest(".assistant-confirmation") as? HTMLElement ?: return@addEventListener
                val pendingId = card.getAttribute("data-pending-id").orEmpty()
                val decision = target.getAttribute("data-decision").orEmpty()
                if (pendingId.isBlank() || decision.isBlank()) return@addEventListener
                card.querySelectorAll("button").let { nodes -> for (j in 0 until nodes.length) (nodes.item(j) as? HTMLElement)?.setAttribute("disabled", "true") }
                messages.innerHTML += "<div id=\"assistant-thinking\" class=\"assistant-message\">Applying your decision…</div>"
                setAssistantWaiting(true)
                promise(FirebaseGateway.callable("askSupportAssistant", json("pendingActionId" to pendingId, "decision" to decision)), { result ->
                    setAssistantWaiting(false)
                    element("assistant-thinking")?.remove()
                    card.querySelector(".assistant-confirm-actions")?.textContent = if (decision == "confirm") "Confirmed" else "Cancelled"
                    appendAssistantResult(messages, result.data)
                }, { message ->
                    setAssistantWaiting(false)
                    element("assistant-thinking")?.remove()
                    card.querySelectorAll("button").let { nodes -> for (j in 0 until nodes.length) (nodes.item(j) as? HTMLElement)?.removeAttribute("disabled") }
                    toast(message, true)
                    saveAssistantHistory(messages)
                })
            })
        }
    }

    private fun setAssistantWaiting(waiting: Boolean) {
        assistantWaiting = waiting
        val button = element("assistant-send") ?: return
        button.setAttribute("title", if (waiting) "Stop response" else "Send")
        button.innerHTML = "<span class=\"material-symbols-rounded\">${if (waiting) "stop" else "send"}</span>"
        button.classList.toggle("btn-danger", waiting)
        button.classList.toggle("btn-primary", !waiting)
    }

    private fun stopAssistantResponse() {
        if (!assistantWaiting) return
        assistantRequestId += 1
        assistantWaiting = false
        element("assistant-thinking")?.remove()
        setAssistantWaiting(false)
        saveAssistantHistory()
    }

    private fun assistantGreetingHtml(): String =
        "<div class=\"assistant-message\">Hi ${escape(currentName)}. Ask me about tickets, incidents, checklists, site visits, users, chats or how the app works.</div>"

    private fun assistantHistoryKey(): String {
        val uid = FirebaseGateway.currentUser()?.uid as? String ?: "anonymous"
        val day = js("new Date().toISOString().slice(0,10)") as String
        return "zi-assistant-history:$uid:$day"
    }

    private fun loadAssistantHistory(): String =
        window.localStorage.getItem(assistantHistoryKey()).orEmpty()

    private fun saveAssistantHistory(messages: HTMLElement? = element("assistant-messages")) {
        val html = messages?.innerHTML.orEmpty()
            .replace(Regex("<div id=\"assistant-thinking\" class=\"assistant-message\">[\\s\\S]*?</div>"), "")
        if (html.isBlank()) window.localStorage.removeItem(assistantHistoryKey())
        else window.localStorage.setItem(assistantHistoryKey(), html.takeLast(180000))
    }

    private fun assistantReplyHtml(raw: String): String {
        val prepared = raw.replace("\r\n", "\n")
            .replace(Regex("[ \\t]+(?=[*•-][ \\t]+)"), "\n")
            .replace(Regex("[ \\t]+(?=\\d+[.)][ \\t]+)"), "\n")
        val html = StringBuilder()
        var openList = ""
        fun closeList() {
            if (openList.isNotEmpty()) html.append("</").append(openList).append(">")
            openList = ""
        }
        fun inline(value: String): String = escape(value)
            .replace(Regex("\\*\\*(.+?)\\*\\*"), "<strong>$1</strong>")
            .replace(Regex("__(.+?)__"), "<strong>$1</strong>")
            .replace("`", "")
            .replace("*", "")
        prepared.lines().forEach { source ->
            val line = source.trim()
            when {
                line.isBlank() -> closeList()
                line.matches(Regex("^#{1,6}\\s+.*")) -> {
                    closeList()
                    html.append("<h4>").append(inline(line.replaceFirst(Regex("^#{1,6}\\s+"), ""))).append("</h4>")
                }
                line.matches(Regex("^[*•-]\\s+.*")) -> {
                    if (openList != "ul") { closeList(); html.append("<ul>"); openList = "ul" }
                    html.append("<li>").append(inline(line.replaceFirst(Regex("^[*•-]\\s+"), ""))).append("</li>")
                }
                line.matches(Regex("^\\d+[.)]\\s+.*")) -> {
                    if (openList != "ol") { closeList(); html.append("<ol>"); openList = "ol" }
                    html.append("<li>").append(inline(line.replaceFirst(Regex("^\\d+[.)]\\s+"), ""))).append("</li>")
                }
                else -> { closeList(); html.append("<p>").append(inline(line)).append("</p>") }
            }
        }
        closeList()
        return html.toString()
    }

}

internal fun applyTheme(mode: String) {
    val dark = mode == "dark" || (mode == "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)
    document.documentElement?.setAttribute("data-theme", if (dark) "dark" else "light")
}

fun main() {
    installSelectPickers()
    if (window.location.pathname.startsWith("/customer")) CustomerPortal.start() else WebApp.start()
}
